import { DOCUMENT } from '@angular/common';
import { Injectable, computed, inject, signal, RendererFactory2 } from '@angular/core';

type Theme = 'light' | 'dark';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private document = inject(DOCUMENT);
  private renderer = inject(RendererFactory2).createRenderer(null, null);
  theme = signal<Theme>((localStorage.getItem('app-theme') as Theme) || 'light');

  /** Chart colours resolved from the active theme's CSS custom properties. */
  readonly chartPalette = computed(() => {
    this.theme();
    const style = getComputedStyle(this.document.body);
    return {
      text: style.getPropertyValue('--text-color') || '#495057',
      textSecondary: style.getPropertyValue('--text-color-secondary') || '#6c757d',
      surfaceBorder: style.getPropertyValue('--surface-border') || '#dee2e6',
      primary: style.getPropertyValue('--primary-color') || '#14b8a6',
      primaryDark: style.getPropertyValue('--primary-dark-color') || '#0f766e',
    };
  });

  constructor() {
    // Applied synchronously rather than from an effect: whatever derives colours from CSS custom
    // properties must see the class already on <body> when it reads them.
    this.updateBodyClass(this.theme());
  }

  private updateBodyClass(theme: Theme) {
    if (theme === 'dark') {
      this.renderer.addClass(this.document.body, 'dark-mode');
    } else {
      this.renderer.removeClass(this.document.body, 'dark-mode');
    }
  }

  toggleTheme() {
    const next: Theme = this.theme() === 'light' ? 'dark' : 'light';
    this.updateBodyClass(next);
    localStorage.setItem('app-theme', next);
    this.theme.set(next);
  }
}
